import React, { Key, useState, useCallback, useEffect } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Plus } from '@phosphor-icons/react';
import {
  ButtonCommon,
  IconSort,
  ColumnSelector,
  TableCommon,
  StatusDotCommon,
  ExcelOperationsHandler,
  showAlertNotification,
  AddDiscountCodeModal,
} from 'components/common';
import { mockDataDiscountCode } from 'constant/MockData';
import { Columns, QueryParams } from 'types';
import { DiscountCodeRecord } from 'types/common';
import { Form } from 'antd';
import { useGenerateCodeMutation } from 'services';
import { DiscountCode } from 'models/DiscountCode';
import { buildFullParams, parseSimpleFilter } from 'utils/filterBuilder';
import { useAppDispatch, useAppSelector } from 'hooks';
import { setTableParams, TableCacheItem } from 'stores/slices/tableCacheSlice';

const DiscountCodeListPage: React.FC = () => {
  const [discountCodes, setDiscountCodes] = useState<DiscountCodeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const pageKey = 'discount-list';
  const dispatch = useAppDispatch();
  const tableState = useAppSelector((s) => s.tableCache[pageKey]) as TableCacheItem<DiscountCodeRecord> | undefined;
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>({
    size: tableState?.pageSize ?? 10,
    page: tableState?.page ?? 1,
    sort: tableState?.sortField
      ? `${String(tableState.sortField)}:${tableState.sortOrder === 'descend' ? 'desc' : 'asc'}`
      : 'updatedAt:desc',
    filter: tableState?.filter ?? '',
  });
  const [generateCode] = useGenerateCodeMutation();
  const [discountToEdit, setDiscountToEdit] = useState<DiscountCodeRecord | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Load mock data
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const discountCodeRecords = mockDataDiscountCode.map((disc) => ({
        key: disc.id,
        id: disc.id,
        code: disc.code,
        discountCode: disc.discountCode,
        discountValue: disc.discountValue,
        description: disc.description,
        isActive: disc.isActive,
      }));

      setDiscountCodes(discountCodeRecords);
      dispatch(
        setTableParams({
          pageKey,
          params: {
            lastFetched: Date.now(),
            search: filteredQuery.searchValue ?? '',
            page: filteredQuery.page ?? 1,
            pageSize: filteredQuery.size ?? 10,
            filter: filteredQuery.filter ?? '',
          } as unknown as Partial<TableCacheItem<unknown>>,
        }),
      );
      setLoading(false);
    }, 500); // mock load time
  }, [filteredQuery]);

  // define columns
  const discountColumns: ColumnsType<DiscountCodeRecord> = [
    {
      title: 'MÃ',
      dataIndex: 'code',
      key: 'code',
      sorter: (a: DiscountCodeRecord, b: DiscountCodeRecord) => a.code.localeCompare(b.code),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (text) => (
        <div className='font-mono' style={{ fontWeight: 600, color: 'var(--textBlack)' }}>
          {text}
        </div>
      ),
      filterSearch: true,
    },
    {
      title: 'CODE',
      dataIndex: 'discountCode',
      key: 'discountCode',
      sorter: (a: DiscountCodeRecord, b: DiscountCodeRecord) => a.discountCode.localeCompare(b.discountCode),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (text) => <div className='font-mono font-medium'>{text}</div>,
      filterSearch: true,
    },
    {
      title: '% GIẢM GIÁ',
      dataIndex: 'discountValue',
      key: 'discountValue',
      sorter: (a: DiscountCodeRecord, b: DiscountCodeRecord) => a.discountValue - b.discountValue,
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (value: number) => <div className='font-medium'>{value}%</div>,
      filterSearch: false,
    },
    {
      title: 'MÔ TẢ',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      sorter: (a: DiscountCodeRecord, b: DiscountCodeRecord) =>
        (a.description || '').localeCompare(b.description || ''),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (text) => <div className='line-clamp-2'>{text}</div>,
      filterSearch: true,
    },
    {
      title: 'TRẠNG THÁI',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: (a: DiscountCodeRecord, b: DiscountCodeRecord) => (a.isActive === b.isActive ? 0 : a.isActive ? 1 : -1),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (isActive: boolean) => {
        return isActive ? (
          <StatusDotCommon dotBgColor='bg-[var(--sematicSuccessColor500)]' label='Đang kích hoạt' />
        ) : (
          <StatusDotCommon dotBgColor='bg-[var(--sematicErrorColor500)]' label='Ngừng kích hoạt' />
        );
      },
      filterSearch: false,
    },
  ];

  const discountColumnsCheck: Columns[] = discountColumns.map((column) => ({
    key: column.key as string,
    title: column.title as string,
  }));

  const [checkedList, setCheckedList] = useState<(Key | undefined)[]>(discountColumns.map((column) => column.key));

  const getFilteredColumns = () => {
    return discountColumns.filter((item) => checkedList.includes(item.key));
  };

  const handlePagination = useCallback(
    (query: QueryParams) => {
      const filterQuery = buildFullParams({
        ...query,
      });
      setFilteredQuery((prev) => ({
        ...prev,
        ...filterQuery,
      }));
    },
    [filteredQuery],
  );

  const handleAddNew = () => {
    setIsModalOpen(true);
    // Reset form và discount code to edit
    setDiscountToEdit(null);
    form.resetFields();
  };

  const handleGenerate = useCallback(async () => {
    try {
      const prefix = 'GG';
      const generatedCode = await generateCode({ code: prefix }).unwrap();
      return generatedCode;
    } catch (error) {
      console.error('Error generating code:', error);
      const prefix = 'GG';
      const timestamp = Date.now().toString().slice(-6);
      const generatedCode = `${prefix}${timestamp}`;
      return generatedCode;
    }
  }, [generateCode]);

  const handleStatusToggle = useCallback(async (checked: boolean, record: DiscountCodeRecord) => {
    // Mock API call
    setLoading(true);
    setTimeout(() => {
      setDiscountCodes((prev) => prev.map((item) => (item.id === record.id ? { ...item, isActive: checked } : item)));
      setLoading(false);

      showAlertNotification({
        type: 'success',
        title: 'Thao tác thành công',
        message: `Mã giảm giá ${record.discountCode} đã được ${checked ? 'kích hoạt' : 'vô hiệu hóa'} thành công`,
      });
    }, 500);
  }, []);

  const handleEdit = useCallback(
    (record: DiscountCodeRecord) => {
      setDiscountToEdit(record);
      setIsModalOpen(true);
      form.setFieldsValue({
        code: record.code,
        discountCode: record.discountCode,
        discountValue: record.discountValue,
        description: record.description,
        isActive: record.isActive,
      });
    },
    [form],
  );

  const handleModalSubmit = (values: Partial<DiscountCode>) => {
    console.log('Submit values:', values);

    // Mock API call
    setLoading(true);
    setTimeout(() => {
      if (discountToEdit) {
        // Update existing discount code
        setDiscountCodes((prev) =>
          prev.map((item) =>
            item.id === discountToEdit.id
              ? {
                  ...item,
                  ...values,
                }
              : item,
          ),
        );
        showAlertNotification({
          type: 'success',
          title: 'Thao tác thành công',
          message: `Mã giảm giá ${values.discountCode} đã được cập nhật thành công`,
        });
      } else {
        // Add new discount code
        const newId = `disc-${Date.now().toString().slice(-5)}`;
        const newDiscountCode = {
          id: newId,
          key: newId,
          code: values.code || '',
          discountCode: values.discountCode || '',
          discountValue: values.discountValue || 0,
          description: values.description || '',
          isActive: values.isActive || false,
        };
        setDiscountCodes((prev) => [...prev, newDiscountCode]);
        showAlertNotification({
          type: 'success',
          title: 'Thao tác thành công',
          message: `Mã giảm giá ${values.discountCode} đã được thêm thành công`,
        });
      }
      setLoading(false);
      setIsModalOpen(false);
      form.resetFields();
    }, 500);
  };

  return (
    <div className='flex flex-col h-full relative'>
      {/* Header */}
      <div className='w-full bg-white border-b border-gray-200'>
        <div className='flex justify-between items-center px-6 py-4'>
          <h1 className='text-xl font-bold text-[var(--textBlack100)]'>DANH SÁCH MÃ GIẢM GIÁ</h1>

          <div className='flex gap-2'>
            <ColumnSelector
              allColumns={discountColumnsCheck}
              selectedColumns={checkedList}
              onChange={setCheckedList}
              defaultColumnChecked='code'
            />

            <ExcelOperationsHandler entityType='discount-code' entityName='Mã giảm giá' />

            <ButtonCommon icon={<Plus size={20} />} color='primary' onClick={handleAddNew} size='xs' radius='rounded'>
              Thêm mã mới
            </ButtonCommon>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='flex-grow bg-white'>
        <TableCommon<DiscountCodeRecord>
          columns={getFilteredColumns()}
          dataSource={discountCodes}
          defaultPageSize={10}
          onEdit={handleEdit}
          onCopy={handleEdit}
          onDelete={() => {}}
          count={mockDataDiscountCode.length}
          searchable={true}
          handleToggleStatus={handleStatusToggle}
          tableProps={{
            scroll: { x: 'max-content' },
            size: 'large',
            loading: loading,
            style: {
              minHeight: `calc(100vh - 275px)`,
              display: 'flex',
              flexDirection: 'column',
              padding: '0',
            },
          }}
          onPaginationChange={handlePagination}
          currentPage={Number(filteredQuery.page)}
          searchColumnDefault={parseSimpleFilter(tableState?.filter)}
        />
      </div>

      <AddDiscountCodeModal
        isOpen={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
        }}
        onSubmit={handleModalSubmit}
        form={form}
        onGenerate={handleGenerate}
      />
    </div>
  );
};

export default DiscountCodeListPage;
